import classNames from "classnames";
import { AppKind } from "common/components/app-kind";
import type { ConfirmArgs } from "common/components/confirm-modal";
import { confirmModalTopic } from "common/components/confirm-modal";
import type { AppFeatures } from "common/components/SerializedApp";
import { getAppKindFromFeatures } from "common/components/SerializedApp";
import { isWireApp } from "common/is-wire-app";
import PubSub from "pubsub-js";
import * as React from "react";
import WireModal from "webapp/ui/views/wire-modal/wire-modal-view";
import { APP_MODAL_ROOT, APP_ROOT, FLYOUT_ROOT, PUSH_MODAL_ROOT } from "../../../lib/constants";
import { standalone } from "../../../lib/device";
import ConfirmModal from "../confirm-modal/confirm-modal";
import { AppRoot, AppStyle, FlyoutContainer, ModalContainer, ModalRoot, PushModalContainer } from "./app-style";

type ConfirmArgsWithResolve = ConfirmArgs & { resolve: (result: boolean) => void };

interface Props {
    className?: string;
    children: React.ReactNode;
    refCallback?: ((element: HTMLElement) => void) & string;
    appFeatures: AppFeatures;
}

interface State {
    confirmArgs: ConfirmArgsWithResolve | undefined;
}

export default class App extends React.PureComponent<Props, State> {
    public state: State = {
        confirmArgs: undefined,
    };

    private token: any;
    public componentDidMount() {
        this.token = PubSub.subscribe(confirmModalTopic, (_method: string, args: ConfirmArgs) => {
            const result = new Promise<boolean>(resolve => {
                this.setState({
                    confirmArgs: {
                        ...args,
                        resolve,
                    },
                });
            });

            args.awaitable = result;
        });
    }

    public componentDidUpdate(_: Props, prevState: State) {
        const { confirmArgs } = prevState;
        // If we're replacing confirm modals, reject the last visible one.
        if (confirmArgs !== undefined && confirmArgs !== this.state.confirmArgs) {
            confirmArgs.resolve(false);
        }
    }

    public componentWillUnmount() {
        PubSub.unsubscribe(this.token);
    }

    public render() {
        const { className, children, refCallback, appFeatures } = this.props;
        const isBuilder = className ? className.includes("builder") : false;
        const appKind = getAppKindFromFeatures(appFeatures);
        const isWire = isWireApp(appFeatures);

        const { confirmArgs } = this.state;
        let confirm: React.ReactNode;

        if (confirmArgs !== undefined) {
            if (appKind === AppKind.Page) {
                confirm = (
                    <WireModal
                        title={confirmArgs.title}
                        accept={confirmArgs.cancel ?? "Continue"}
                        cancel={confirmArgs.accept}
                        description={confirmArgs.description}
                        onFinish={(accepted: boolean) => {
                            confirmArgs.resolve(accepted);
                            this.setState({ confirmArgs: undefined });
                        }}
                    />
                );
            } else {
                confirm = (
                    <ConfirmModal
                        title={confirmArgs.title}
                        description={confirmArgs.description}
                        accept={confirmArgs.accept}
                        cancel={confirmArgs.cancel}
                        modalStyle={confirmArgs.style}
                        onFinish={(accepted: boolean) => {
                            confirmArgs.resolve(accepted);
                            this.setState({ confirmArgs: undefined });
                        }}
                    />
                );
            }
        }

        return (
            <AppStyle isBuilder={isBuilder} isWireApp={isWire}>
                <AppRoot
                    isAppKindPage={appKind === AppKind.Page}
                    id={APP_ROOT}
                    className={classNames(className, { standalone })}
                    ref={refCallback as any}>
                    {children}
                    <PushModalContainer>
                        <ModalRoot id={PUSH_MODAL_ROOT} />
                    </PushModalContainer>
                    <ModalContainer>
                        <ModalRoot id={APP_MODAL_ROOT}>{confirm !== undefined && !isWire && confirm}</ModalRoot>
                    </ModalContainer>
                    <FlyoutContainer>
                        <ModalRoot id={FLYOUT_ROOT}>{confirm !== undefined && isWire && confirm}</ModalRoot>
                    </FlyoutContainer>
                </AppRoot>
            </AppStyle>
        );
    }
}
